"use client";

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Heart,
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import { getWithoutTokenAPI } from "../util/Api";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "./ui/alert-dialog";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage } from "../redux/navigationSlice.js";
import AOS from "aos";
import "aos/dist/aos.css";
import { useTranslation } from "react-i18next";
import TermsAndConditions from "./TermsAndConditions.jsx";

export function Footer({ onNavigate }) {
  const [footer, setFooter] = useState(null);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.user);
  const token = data?.userId?.accessToken;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

 const officeaddress=i18n.language === 'en'?
  footer?.OfficeAddress?.en:footer?.OfficeAddress?.ta;
  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const response = await getWithoutTokenAPI(`/footer/get_all`);
        if (response?.data?.length) {
          setFooter(response.data[0]);
        }
      } catch (error) {
        console.error("Error fetching footer:", error);
      }
    };
    fetchFooter();
  }, []);

  if (!footer) return null;

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand Info */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              {footer?.brandInfo?.logo ? (
                <img
                  src={footer?.brandInfo?.logo}
                  alt="AvanAval Logo"
                  className="w-16 h-16"
                />
              ) : (
                <Heart className="h-8 w-8 text-red-500" />
              )}
              <span className="text-xl font-semibold">
                {"அவன்அவள்" || footer?.brandInfo?.brandName}
              </span>
            </div>
            <p className="text-primary-foreground/80 mb-4">
             {t("Your journey to finding the right Tamil partner begins with Aval Aval.")}

            </p>
            <p className="text-primary-foreground/60 mb-4">
              {footer?.brandInfo?.yearsActive}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="mb-4 font-semibold">{t("Quick Links")}</h4>
            <ul className="space-y-2 text-primary-foreground/80">
              <li>
                <button
                  onClick={() => 
                    {window.scrollTo({ top: 0, behavior: "smooth" })
                    navigate("/browse")}}
                  className="hover:text-red-400 transition-colors cursor-pointer text-left"
                >
                  {t("Browse")}
                </button>
              </li>
              <li>
                <button
                  onClick={
                    token
                      ? () => {window.scrollTo({ top: 0, behavior: "smooth" });navigate("/matches")}
                      : () => setShowLoginDialog(true)
                  }
                  className="hover:text-red-400 transition-colors cursor-pointer text-left"
                >
                  {t("Matches")}
                </button>
              </li>
              <li>
                <button
                  onClick={
                    token
                      ? () => {window.scrollTo({ top: 0, behavior: "smooth" });navigate("/messages")}
                      : () => setShowLoginDialog(true)
                  }
                  className="hover:text-red-400 transition-colors cursor-pointer text-left "
                >
                  {t("Messages")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => {window.scrollTo({ top: 0, behavior: "smooth" });navigate("/subscription")}}
                  className="hover:text-red-400 transition-colors cursor-pointer text-left "
                >
                  {t("Subscription")}
                </button>
              </li>
              <li>
                <button
                  onClick={
                    token
                      ? () => {window.scrollTo({ top: 0, behavior: "smooth" });navigate("/profile")}
                      : () => setShowLoginDialog(true)
                  }
                  className="hover:text-red-400 transition-colors cursor-pointer text-left"
                >
                  {t("Profile")}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="mb-4 font-semibold">{t("Contact Us")}</h4>
            <div className="space-y-3 text-primary-foreground/80">
              <div className="flex items-start space-x-3">
                <Phone className="h-4 w-4 mt-1 text-red-400" />
                <div>
                  <p className="font-medium">{t("Phone Number")}</p>
                  <p className="hover:text-red-400 transition-colors cursor-pointer">
                    {footer?.PhoneNumber}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Mail className="h-4 w-4 mt-1 text-red-400" />
                <div>
                  <p className="font-medium">{t("Email Address")}</p>
                  <p className="hover:text-red-400 transition-colors cursor-pointer">
                    {footer?.EmailAddress}
                  </p>
                </div>
              </div>
              {footer?.officeaddress && <div className="flex items-start space-x-3">
                <MapPin className="h-4 w-4 mt-1 text-red-400" />
                <div>
                  <p className="font-medium">{t("Office Address")}</p>
                  <p className="text-sm">
                    {officeaddress}
                  </p>
                </div>
              </div>}
            </div>
          </div>

          {/* Social Links */}
          <div>
            <h4 className="mb-4 font-semibold">{t("Follow Us")}</h4>
            <div className="flex space-x-4">
              {footer?.socialMedia?.facebook && (
                <a
                  href={footer.socialMedia.facebook}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Facebook className="h-5 w-5 hover:text-red-400 cursor-pointer transition-colors" />
                </a>
              )}
              {footer?.socialMedia?.twitter && (
                <a
                  href={footer.socialMedia.twitter}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Twitter className="h-5 w-5 hover:text-red-400 cursor-pointer transition-colors" />
                </a>
              )}
              {footer?.socialMedia?.instagram && (
                <a
                  href={footer.socialMedia.instagram}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Instagram className="h-5 w-5 hover:text-red-400 cursor-pointer transition-colors" />
                </a>
              )}
              {footer?.socialMedia?.youtube && (
                <a
                  href={footer.socialMedia.youtube}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Youtube className="h-5 w-5 hover:text-red-400 cursor-pointer transition-colors" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-primary-foreground/20 mt-8 pt-8 text-center text-primary-foreground/60">
          <p style={{width:"100%",display:"flex",justifyContent:"center"}}>
            {footer?.copyright ||"© 2025 AvanAval"} | <span className="inline-flex items-center gap-1">Powered by<a className="cursor-pointer hover:text-red-400 " href="https://prosperoustech.com/" target="_blank">ProsperousTech</a></span> | {<p className="cursor-pointer hover:text-red-400 " onClick={()=>{window.scrollTo({ top: 0, behavior: "smooth" });navigate('/t&c')}}>Terms</p>} | {<p className="cursor-pointer hover:text-red-400 " onClick={()=>{window.scrollTo({ top: 0, behavior: "smooth" });navigate('/privacy')}}>Privacy Policy</p>}
          </p>
        </div>
      </div>

      <AlertDialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("You’re Almost There!")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("Log in to explore profiles and start connecting")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setShowLoginDialog(false)}
              className="cursor-pointer"
            >
              {t("Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white cursor-pointer"
              onClick={() => {
                setShowLoginDialog(false);
                dispatch(setCurrentPage("login"));
              }}
            >
              {t("Login")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </footer>
  );
}
